import { logger as browserLogger } from '@/logger/logger.browser';

/**
 * Decodes a JWT token and returns its payload as a JSON object.
 * Does NOT verify signature or claims—for display/UI only.
 * Never use for authorization. Use getVerifiedEmailFromSessionToken (server-side) instead.
 *
 * @param idToken The JWT token to decode.
 * @returns The decoded payload, or null if the token is invalid.
 */
export const decodeToken = (
  idToken: string | null,
): Record<string, any> | null => {
  if (!idToken) return null;

  try {
    const [, dataPart] = idToken.split('.');
    if (!dataPart) throw new Error('Invalid token format');
    return JSON.parse(atob(dataPart));
  } catch (error) {
    browserLogger.warn('Error decoding Auth token', {
      error: JSON.stringify(error),
    });
    return null;
  }
};

/**
 * Extracts the email (sub claim) from a decoded token. Does NOT verify the token.
 * For display/UI only—never use for authorization. API routes must use
 * getVerifiedEmailFromSessionToken (server-side) to get a verified email.
 *
 * @param token The JWT token to decode.
 * @returns The email address if present, or an empty string.
 */
export const decodeTokenToEmail = (token: string): string => {
  const decoded = decodeToken(token);

  if (decoded && typeof decoded === 'object' && 'sub' in decoded) {
    return decoded.sub as string;
  }

  return '';
};
